import type { SimConfig } from '../config/simConfig';
import { fnv1a64 } from '../math/hash';

// Canonical SimConfig JSON (§17): stable key order so the same config always serializes identically
// (⇒ the same hash). Delivered in Welcome/MATCH_START and embedded in the log header; the predictor
// and replay build `cfg` ONLY from this JSON, never bundle defaults.

function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as Record<string, unknown>).sort()) out[k] = sortKeys((v as Record<string, unknown>)[k]);
    return out;
  }
  return v;
}

export function canonicalConfigJson(cfg: SimConfig): string {
  return JSON.stringify(sortKeys(cfg));
}

/** Parse a canonical config JSON back into a SimConfig (structure matches; used by replay/predictor). */
export function parseConfigJson(json: string): SimConfig {
  return JSON.parse(json) as SimConfig;
}

/** 8-byte config hash (FNV-1a 64 over the canonical JSON) as a Uint8Array. (Server may use sha256 32B.) */
export function configHashBytes(cfg: SimConfig): Uint8Array {
  const hex = fnv1a64(new TextEncoderLite().encode(canonicalConfigJson(cfg)));
  const out = new Uint8Array(8);
  for (let i = 0; i < 8; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

// tiny ASCII/utf8 encoder (avoids DOM/node TextEncoder types in shared)
class TextEncoderLite {
  encode(s: string): Uint8Array {
    const out: number[] = [];
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i);
      if (c < 0x80) out.push(c);
      else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
      else out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
    return new Uint8Array(out);
  }
}
